"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowRight } from "lucide-react";

export interface TalktribeHeroBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    exploreText?: string;
    exploreHref?: string;
    platform?: string;
    languages?: string;
    scope?: string;
    imageUrl?: string;
    imageAlt?: string;
    domainLabel?: string;
}

export default function TalktribeHeroBlock({
    tag = "AI Character Platform",
    title = "Chat with AI Characters — or Create Your Own",
    description = "TalkTribe is an AI chatbot platform where anyone can discover pre-made characters or create new ones using simple prompts. Each character has persistent memory, unique personality, and speaks in 40+ languages.",
    exploreText = "Explore Features",
    exploreHref = "#features",
    platform = "Web App",
    languages = "40+ Supported",
    scope = "Full Product",
    imageUrl = "/images/talktribe_product.png",
    imageAlt = "TalkTribe — AI Character Chatbot Platform",
    domainLabel = "talktribe.app"
}: TalktribeHeroBlockProps) {
    return (
        <section className="relative min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24 xl:px-32 flex items-center border-b border-zinc-900 bg-[#060608] w-full overflow-hidden" data-header-theme="light">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[140px] pointer-events-none" />
            <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-fuchsia-600/8 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto z-10 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

                    <div className="lg:col-span-5 flex flex-col justify-center">
                        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="space-y-6">
                            {tag && (
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/30 w-fit backdrop-blur-md">
                                    <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                                    <span className="text-[10px] font-mono text-violet-300 uppercase tracking-widest font-bold">{tag}</span>
                                </div>
                            )}

                            <h1 className="text-4xl md:text-5xl xl:text-6xl font-light tracking-tight text-white leading-[1.05]">
                                {title === "Chat with AI Characters — or Create Your Own" ? (
                                    <>
                                        Chat with <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400">AI Characters</span> — or Create Your Own
                                    </>
                                ) : (
                                    title
                                )}
                            </h1>

                            <p className="text-base text-zinc-400 leading-relaxed max-w-xl font-light">
                                {description}
                            </p>

                            <div className="pt-4 flex flex-wrap gap-4 items-center">
                                <a href={exploreHref} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white transition-all duration-300 font-mono text-[11px] uppercase tracking-wider font-semibold rounded-lg shadow-lg hover:shadow-violet-500/20 w-fit">
                                    {exploreText} <ArrowRight className="w-3.5 h-3.5" />
                                </a>
                            </div>
                        </motion.div>

                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
                            className="mt-8 grid grid-cols-3 gap-6 text-xs font-mono border-t border-zinc-900 pt-6">
                            <div className="flex flex-col gap-1">
                                <span className="text-zinc-600 text-[10px] uppercase tracking-wider">Platform</span>
                                <span className="text-zinc-300">{platform}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-zinc-600 text-[10px] uppercase tracking-wider">Languages</span>
                                <span className="text-zinc-300">{languages}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-zinc-600 text-[10px] uppercase tracking-wider">Scope</span>
                                <span className="text-zinc-300">{scope}</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Hero Product Screenshot */}
                    <div className="lg:col-span-7 w-full flex justify-center z-10">
                        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
                            className="w-full max-w-2xl relative">
                            <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950/80 p-2 shadow-[0_30px_70px_rgba(0,0,0,0.8)]">
                                <div className="w-full h-7 bg-zinc-900 border border-zinc-800 rounded-t-lg flex items-center px-4 gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                                    <div className="h-4 bg-zinc-950 px-6 rounded text-[9px] font-mono text-zinc-500 flex items-center mx-auto border border-zinc-900">
                                        {domainLabel}
                                    </div>
                                </div>
                                <div className="relative w-full aspect-[960/457] rounded-b-lg overflow-hidden border border-zinc-900 bg-[#070319]">
                                    <Image
                                        src={imageUrl}
                                        alt={imageAlt}
                                        fill
                                        sizes="(max-width: 1024px) 100vw, 56vw"
                                        className="object-contain object-center"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}
